import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {SchemaTag} from 'folder-items/FileConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {BioentityLinkSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  ProcedureEquipmentPlannedValue,
  ProcedureMaterialInputPlannedValue,
  MethodNode,
} from 'procedures/ProcedureDomainTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {BioentityItemsQuery} from './__generated/BioentityItemsQuery';

export const BIOENTITY_ITEMS_QUERY = gql`
  query BioentityItemsQuery($bioentityIds: [StrictString!]!) @skipAudit @engComponent(name: PROCEDURES) {
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        id
        tagsJson
        tagSchema {
          ... on TagSchemaNode {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface BioentityForProcedure {
  id: string;
  tags: Array<SchemaTag>;
  tagSchema: {
    id: string;
  } | null;
}

export default function useBioentitiesForProcedures(
  bioentityIds: Array<string>
): Array<BioentityForProcedure> | null {
  const result = useSkippableBenchlingQuery<BioentityItemsQuery>(BIOENTITY_ITEMS_QUERY, {
    variables: {bioentityIds},
  });

  if (result.type === QueryResultType.SUCCESS) {
    return accessibleObjs(result.data.bioentities).map((bioentity) => ({
      id: bioentity.id,
      tags: bioentity.tagsJson,
      tagSchema:
        bioentity.tagSchema != null
          ? {
              id: bioentity.tagSchema.id,
            }
          : null,
    }));
  }

  return null;
}

function getPlannedValueBioentityIdsFromParameterSetting<
  TPlannedValue extends ProcedureMaterialInputPlannedValue | ProcedureEquipmentPlannedValue,
>(
  values: Array<TPlannedValue>,
  getPlannedValueTypedValue: (plannedValue: TPlannedValue) => TypedValue | null,
  assertionExtraInfo: object
): Array<string> {
  return values
    .map((pv: TPlannedValue) => getPlannedValueTypedValue(pv))
    .filter(
      (typedValue): typedValue is BioentityLinkSchemaFieldTypedValue =>
        typedValue?.type === FieldType.BIOENTITY_LINK && typedValue.targetFiles.length > 0
    )
    .flatMap((typedValue): Array<string> =>
      typedValue.targetFiles.map((targetFile): string => {
        const entityId = targetFile.id;
        assert(entityId != null, 'Planned value entity id should exist', {
          parameterSetting: assertionExtraInfo,
        });
        return entityId;
      })
    );
}

/**
 * Utility function to return bioentity ids from method nodes, most commonly found in parameter settings.
 * Can be extended to other entity links in method nodes.
 */
export function getBioentityIdsFromMethods(methodNodes: Array<MethodNode>): Array<string> {
  return methodNodes.flatMap((methodNode) => {
    const materialInputBioentityIds = methodNode.materialInputSettings.flatMap((materialInputSetting) =>
      getPlannedValueBioentityIdsFromParameterSetting(
        materialInputSetting.values,
        (pv) => pv.material,
        materialInputSetting
      )
    );

    const equipmentBioentityIds = methodNode.equipmentSettings.flatMap((equipmentSetting) =>
      getPlannedValueBioentityIdsFromParameterSetting(
        equipmentSetting.values,
        (pv) => pv.equipment,
        equipmentSetting
      )
    );

    const equipmentModelsBioentityIds = methodNode.equipmentSettings.flatMap((equipmentSetting) => {
      const models = equipmentSetting.equipmentVersion.models;
      const isBioentityLinkType = models.type === FieldType.BIOENTITY_LINK;
      if (isBioentityLinkType) {
        return models.targetFiles
          .filter((targetFile) => targetFile.id != null)
          .map((targetFile) => targetFile.id) as Array<string>;
      }
      return [];
    });

    return [
      ...new Set([...materialInputBioentityIds, ...equipmentBioentityIds, ...equipmentModelsBioentityIds]),
    ];
  });
}
